
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function ResourcesHero() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Premium Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/1dfa66043c33216f1360d35e8a58ce79.jpg"
          alt="Resources background"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-900/90 to-amber-950/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 right-20 w-96 h-96 bg-amber-500/15 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-20 left-20 w-80 h-80 bg-teal-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 pt-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-full mb-8">
              <i className="ri-book-open-line text-amber-400" />
              <span className="text-amber-300 text-sm font-semibold">Knowledge Hub • 200+ Resources</span>
            </div>

            <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight mb-6">
              Master Your
              <span className="block bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500 bg-clip-text text-transparent">
                Trade Operations
              </span>
            </h1>

            <p className="text-xl text-slate-300 leading-relaxed mb-8 max-w-xl">
              Comprehensive guides, video tutorials, and expert insights designed to accelerate your trade business growth and streamline your invoicing workflow.
            </p>

            {/* Stats */}
            <div className="flex flex-wrap gap-8 mb-10">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl px-6 py-4">
                <div className="text-3xl font-bold bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">45+</div>
                <div className="text-slate-400 text-sm">Free Guides</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl px-6 py-4">
                <div className="text-3xl font-bold bg-gradient-to-r from-teal-400 to-emerald-500 bg-clip-text text-transparent">18</div>
                <div className="text-slate-400 text-sm">Video Tutorials</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl px-6 py-4">
                <div className="text-3xl font-bold bg-gradient-to-r from-rose-400 to-pink-500 bg-clip-text text-transparent">15K+</div>
                <div className="text-slate-400 text-sm">Downloads</div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <a
                href="#guides"
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 rounded-full font-bold hover:from-amber-500 hover:to-orange-600 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 hover:scale-105"
              >
                <span>Explore Resources</span>
                <i className="ri-arrow-down-line" />
              </a>
              <Link
                to="/blog"
                className="inline-flex items-center gap-3 px-8 py-4 border border-white/30 text-white rounded-full font-semibold hover:bg-white/10 hover:border-amber-500/30 transition-all duration-300 whitespace-nowrap hover:scale-105"
              >
                <span>Read Blog</span>
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>

          {/* Right Visual */}
          <div
            className={`hidden lg:block transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="relative">
              {/* Main Card */}
              <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl p-8 shadow-2xl shadow-black/30">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                    <i className="ri-file-download-line text-2xl text-white" />
                  </div>
                  <div>
                    <div className="text-white font-bold text-lg">Featured Guide</div>
                    <div className="text-amber-400 text-sm font-medium">Most Downloaded</div>
                  </div>
                </div>
                <img
                  src="https://readdy.ai/api/search-image?query=premium%20professional%20invoice%20template%20document%20mockup%20showing%20detailed%20itemized%20costs%20business%20branding%20elegant%20typography%20clean%20modern%20design%20warm%20amber%20lighting%20on%20dark%20slate%20background%20high%20quality%203d%20render%20with%20subtle%20shadows&width=600&height=350&seq=featured-guide-premium-v2&orientation=landscape"
                  alt="Featured guide preview"
                  className="w-full h-52 object-cover rounded-2xl mb-6 shadow-lg"
                />
                <h3 className="text-xl font-bold text-white mb-2">Complete Invoicing Handbook</h3>
                <p className="text-slate-400 text-sm mb-4">32 pages • 2.4 MB • PDF</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-amber-400">
                    <i className="ri-download-2-line" />
                    <span className="text-sm font-medium">1,250 downloads</span>
                  </div>
                  <button className="px-5 py-2.5 bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 rounded-xl font-bold hover:from-amber-500 hover:to-orange-600 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/25">
                    Download Free
                  </button>
                </div>
              </div>

              {/* Floating Video Card */}
              <div className="absolute -top-6 -right-6 bg-teal-500/20 backdrop-blur-xl border border-teal-500/30 rounded-2xl p-5 shadow-2xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                    <i className="ri-video-line text-white text-xl" />
                  </div>
                  <div>
                    <div className="text-white font-bold">18 Videos</div>
                    <div className="text-teal-300 text-sm">Watch & Learn</div>
                  </div>
                </div>
              </div>

              {/* Floating FAQ Card */}
              <div className="absolute -bottom-4 -left-6 bg-rose-500/20 backdrop-blur-xl border border-rose-500/30 rounded-2xl p-5 shadow-2xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                    <i className="ri-question-answer-line text-white text-xl" />
                  </div>
                  <div>
                    <div className="text-white font-bold">FAQ Section</div>
                    <div className="text-rose-300 text-sm">Quick Answers</div>
                  </div>
                </div>
              </div>

              {/* Floating Tools Card */}
              <div className="absolute top-1/2 -right-10 bg-violet-500/20 backdrop-blur-xl border border-violet-500/30 rounded-2xl p-4 shadow-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-violet-400 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                    <i className="ri-tools-line text-white" />
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm">Templates</div>
                    <div className="text-violet-300 text-xs">Ready to Use</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
